import { AppDispatch } from '@typings/redux';
import { EditContainerProps } from 'components/officials/Editor/Editor.types';
import { fetchOfficialDetails, updateOfficial, officialDetailsSelector, OfficialForm } from 'store/officials';
import { accessRulesSelector } from 'store/accessRules';
import { useFormatter, useNotification } from 'components/shared/hooks';
import { useEnvironment } from 'components/shared/hooks/useEnvironment/useEnvironment';
import { mapFormValueToPayload, mapFormPayloadToValue } from './Editor.helpers';
import { Editor } from './Editor';
import { ROUTE_OFFICIALS_LIST } from 'config/routes';
import { push, replace } from 'utils/navigation/navigate';
import React, { FC, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';
import { FormikHelpers } from 'formik';

export const EditContainer: FC<EditContainerProps> = ({ officialId }) => {
  const officialDetails = useSelector(officialDetailsSelector);
  const accessRules = useSelector(accessRulesSelector);
  const dispatch = useDispatch<AppDispatch>();
  const { formatApiErrors } = useFormatter();
  const { success, error } = useNotification();
  const [isSubmitted, setSubmitted] = useState(false);
  const officials = useMemo(
    () => officialDetails.official && mapFormPayloadToValue(officialDetails.official),
    [officialDetails.official],
  );
  const { origin } = useEnvironment();
  useEffect(() => {
    batch(async () => {
      const result = await dispatch(fetchOfficialDetails(officialId));
      if (result.error && result.payload.status === 404) {
        replace(ROUTE_OFFICIALS_LIST);
        return;
      }
    });
  }, [officialId, dispatch]);

  const onSave = async (data: OfficialForm, formik: FormikHelpers<OfficialForm>) => {
    const result = await dispatch(updateOfficial(officialId, mapFormValueToPayload(data), origin));
    if (result.error && result.payload.status === 404) {
      setSubmitted(true);
      replace(ROUTE_OFFICIALS_LIST);
      return;
    }
    if (result.error) {
      if (result.payload.response.errors) {
        error('OFFICIALS.EDITOR.MESSAGE.OFFICIAL_SAVE_ERROR');
        formik.setErrors(formatApiErrors(result.payload.response.errors));
      }
      formik.setSubmitting(false);
    } else {
      success('OFFICIALS.EDITOR.MESSAGE.OFFICIAL_SAVE_SUCCESS');
      setSubmitted(true);
      push(ROUTE_OFFICIALS_LIST);
    }
  };

  const onCancel = () => {
    push(ROUTE_OFFICIALS_LIST);
  };
  return (
    <Editor
      initial={officials}
      loading={officialDetails.loading || accessRules.loading}
      disabledTypeAccount
      accessRules={accessRules.items}
      submitted={isSubmitted}
      onSubmit={onSave}
      onCancel={onCancel}
    />
  );
};
